import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, message, Spin } from 'antd';
import CheckoutLayout from '../components/CheckoutLayout';
import { createPayment, fetchCheckoutConfig } from '../api/payment';
import { getCurrencyMeta, amountToGatewayUnits } from '../constants/currency';
import { getDefaultMerchantName, getResultPageURL, getNotifyURL } from '../utils/site';
import './CheckoutPage.css';


function CheckoutForm({ boot, navigate }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const currencyMeta = useMemo(() => getCurrencyMeta(boot.currency), [boot.currency]);
  const isJPY = boot.currency === 'JPY';
  const isCard = boot.currency === 'CC';

  const onFinish = async (values) => {
    const minorAmount = amountToGatewayUnits(values.amount, boot.currency);
    if (minorAmount <= 0) {
      message.error('Please enter a valid amount');
      return;
    }

    const phone = values.phone ? `${currencyMeta.prefix}${values.phone.replace(/\D/g, '')}` : undefined;

    setLoading(true);
    try {
      const returnUrl = getResultPageURL();
      const notifyUrl = getNotifyURL();
      const payload = {
        amount: minorAmount,
        currency: boot.currency,
        customer_name: values.customer_name || undefined,
        customer_email: values.customer_email || undefined,
        customer_phone: phone,
        remark: values.remark || undefined,
        return_url: returnUrl,
        notify_url: notifyUrl,
      };
      if (isJPY) {
        payload.account = (values.alipay_account || '').trim();
      }
      if (isCard) {
        payload.client_id = (values.client_id || '').trim() || undefined;
        payload.device_id = (values.device_id || '').trim() || undefined;
      }

      const res = await createPayment(payload);

      if (res.code !== 0) {
        throw new Error(res.message || 'create payment failed');
      }

      const { gateway_order_no: gatewayOrderNo, pay_url: payURL, merchant_order_no: merchantOrderNo } = res.data || {};
      if (payURL) {
        window.location.href = payURL;
        return;
      }

      navigate(`/pay/status/${gatewayOrderNo}?merchant_order_no=${merchantOrderNo}`);
    } catch (err) {
      message.error(err.message || 'Payment request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout-form-wrap">
      <p className="section-label">{isCard ? 'VISA CARD PAYMENT' : 'PAYMENT DETAILS'}</p>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
      >
        <Form.Item label="Amount" name="amount" rules={[{ required: true, message: 'Amount required' }]}>
          <Input
            className="amount-input"
            placeholder={isJPY ? '1000' : '0.00'}
            inputMode={isJPY ? 'numeric' : 'decimal'}
            prefix={<span className="input-prefix-inline">{currencyMeta.symbol}</span>}
          />
        </Form.Item>

        {isJPY ? (
          <Form.Item
            label="Alipay Account"
            name="alipay_account"
            rules={[{ required: true, message: 'Alipay account required' }]}
          >
            <Input placeholder="user@alipay.com" />
          </Form.Item>
        ) : null}

        <Form.Item label="Customer Name (optional)" name="customer_name">
          <Input placeholder={isCard ? 'crypto' : 'Enter your name'} />
        </Form.Item>

        <Form.Item
          label="Customer Email (optional)"
          name="customer_email"
          rules={[{ type: 'email', message: 'Invalid email' }]}
        >
          <Input placeholder={isCard ? 'u@crypto.com' : 'Enter your email'} />
        </Form.Item>

        <Form.Item label="Customer Phone (optional)" name="phone">
          <Input
            placeholder={isCard ? '65200000' : 'Enter customer phone'}
            inputMode="tel"
            prefix={<span className="input-prefix-inline">{currencyMeta.prefix}</span>}
          />
        </Form.Item>

        {isCard ? (
          <>
            <Form.Item label="Client ID (optional)" name="client_id">
              <Input placeholder="User ID for risk control" />
            </Form.Item>
            <Form.Item label="Device ID (optional)" name="device_id">
              <Input placeholder="Device fingerprint" />
            </Form.Item>
          </>
        ) : null}

        <Form.Item label="Remark (optional)" name="remark">
          <Input placeholder="Payment remark" />
        </Form.Item>

        <Button type="primary" htmlType="submit" className="pay-button" loading={loading} block>
          {isCard ? 'CONTINUE TO VISA CHECKOUT' : 'MAKE PAYMENT'}
        </Button>
      </Form>
    </div>
  );
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [boot, setBoot] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchCheckoutConfig()
      .then((res) => {
        if (cancelled) {
          return;
        }
        const apiName = res.code === 0 ? res.data?.merchant_name : '';
        const merchantName =
          apiName && apiName !== 'Demo Merchant' ? apiName : getDefaultMerchantName();
        const currency = (res.data?.default_currency || 'BRL').toUpperCase();
        setBoot({ merchantName, currency });
      })
      .catch(() => {
        if (!cancelled) {
          setBoot({
            merchantName: getDefaultMerchantName(),
            currency: 'BRL',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <CheckoutLayout merchantName={boot?.merchantName ?? getDefaultMerchantName()} logoText="PG">
      {!boot ? (
        <div className="checkout-loading">
          <Spin size="large" />
        </div>
      ) : (
        <CheckoutForm key={boot.currency} boot={boot} navigate={navigate} />
      )}
    </CheckoutLayout>
  );
}
